import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import type { Editor } from '@tiptap/core';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  CaseSensitive,
  Check,
  ChevronDown,
  ChevronUp,
  Code,
  Heading,
  Highlighter,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Palette,
  Redo2,
  Type,
  Underline as UnderlineIcon,
  Undo2,
  Upload,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const FONT_FAMILY_OPTIONS = [
  { label: 'Sans Serif', value: 'Inter, ui-sans-serif, system-ui' },
  { label: 'Serif', value: 'Georgia, ui-serif, serif' },
  { label: 'Monospace', value: '"Fira Code", ui-monospace, SFMono-Regular, Menlo' },
  { label: 'Casual', value: '"Comic Sans MS", "Comic Sans", cursive' },
] as const;

const FONT_SIZE_OPTIONS = ['12px', '14px', '16px', '18px', '20px', '24px', '30px'] as const;

const COLOR_OPTIONS = [
  { label: 'Default', value: 'inherit' },
  { label: 'Slate', value: '#334155' },
  { label: 'Crimson', value: '#dc2626' },
  { label: 'Amber', value: '#f59e0b' },
  { label: 'Emerald', value: '#10b981' },
  { label: 'Sky', value: '#0ea5e9' },
  { label: 'Indigo', value: '#6366f1' },
  { label: 'Violet', value: '#8b5cf6' },
  { label: 'Rose', value: '#f43f5e' },
] as const;

const HEADING_OPTIONS = [
  { label: 'Paragraph', value: 'paragraph' },
  { label: 'Heading 1', value: 'heading-1' },
  { label: 'Heading 2', value: 'heading-2' },
  { label: 'Heading 3', value: 'heading-3' },
  { label: 'Heading 4', value: 'heading-4' },
] as const;

const ALIGN_OPTIONS = [
  { label: 'Align left', value: 'left', icon: AlignLeft },
  { label: 'Align center', value: 'center', icon: AlignCenter },
  { label: 'Align right', value: 'right', icon: AlignRight },
  { label: 'Justify', value: 'justify', icon: AlignJustify },
] as const;

type TextAlignOption = (typeof ALIGN_OPTIONS)[number]['value'];

interface ToolbarButtonProps {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
}

interface ComposeToolbarProps {
  editor: Editor;
  onInsertInlineImage: (editor: Editor | null, file: File, alt?: string) => Promise<void>;
  variant?: 'default' | 'inline';
}

const toolbarButtonClasses = (isActive?: boolean) =>
  cn(
    'h-7 w-7 p-0 text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring',
    isActive && 'bg-primary/10 text-primary hover:text-primary'
  );

function ToolbarButton({ icon: Icon, label, onClick, isActive, disabled }: ToolbarButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClick}
          disabled={disabled}
          tabIndex={-1}
          className={cn(toolbarButtonClasses(isActive), disabled && 'opacity-50')}
        >
          <Icon className="h-3.5 w-3.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

export function ComposeToolbar({ editor, onInsertInlineImage, variant = 'default' }: ComposeToolbarProps) {
  const isInline = variant === 'inline';
  const [showAdvancedFormatting, setShowAdvancedFormatting] = useState(false);
  const [colorPopoverOpen, setColorPopoverOpen] = useState(false);
  const [headingPopoverOpen, setHeadingPopoverOpen] = useState(false);
  const [fontFamilyPopoverOpen, setFontFamilyPopoverOpen] = useState(false);
  const [fontSizePopoverOpen, setFontSizePopoverOpen] = useState(false);
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [imagePopoverOpen, setImagePopoverOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [isInsertingImage, setIsInsertingImage] = useState(false);

  const linkInputRef = useRef<HTMLInputElement | null>(null);
  const imageUrlInputRef = useRef<HTMLInputElement | null>(null);
  const inlineImageFileInputRef = useRef<HTMLInputElement | null>(null);

  const textStyleAttributes = useMemo(
    () => (editor.getAttributes('textStyle') as Record<string, unknown>) ?? {},
    [editor]
  );

  const currentFontFamily = (textStyleAttributes.fontFamily as string | undefined) ?? 'inherit';
  const currentFontSize = (textStyleAttributes.fontSize as string | undefined) ?? 'inherit';
  const currentColor = (textStyleAttributes.color as string | undefined) ?? 'inherit';
  const activeHeadingLevel = useMemo(
    () => ([1, 2, 3, 4].find((level) => editor.isActive('heading', { level })) ?? null),
    [editor]
  );
  const blockType = activeHeadingLevel ? `heading-${activeHeadingLevel}` : 'paragraph';

  const currentAlignment = useMemo(() => {
    if (editor.isActive({ textAlign: 'justify' })) {
      return 'justify';
    }
    if (editor.isActive({ textAlign: 'right' })) {
      return 'right';
    }
    if (editor.isActive({ textAlign: 'center' })) {
      return 'center';
    }
    return 'left';
  }, [editor]) as TextAlignOption;

  const canUndo = editor.can().chain().focus().undo().run();
  const canRedo = editor.can().chain().focus().redo().run();
  const isLinkActive = editor.isActive('link') ?? false;
  const blockLabel = blockType === 'paragraph' ? 'Paragraph' : `Heading ${activeHeadingLevel}`;
  const currentFontFamilyLabel =
    currentFontFamily === 'inherit'
      ? 'System Default'
      : FONT_FAMILY_OPTIONS.find((option) => option.value === currentFontFamily)?.label ?? 'Custom';
  const currentFontSizeLabel = currentFontSize === 'inherit' ? 'Default Size' : currentFontSize;
  const canInsertImageInline = selectedImageFile !== null || imageUrl.trim() !== '';

  useEffect(() => {
    if (!linkPopoverOpen) {
      return;
    }
    const existingUrl = editor.getAttributes('link').href as string | undefined;
    setLinkUrl(existingUrl ?? '');
    const timer = window.setTimeout(() => linkInputRef.current?.focus(), 10);
    return () => window.clearTimeout(timer);
  }, [linkPopoverOpen, editor]);

  useEffect(() => {
    if (!imagePopoverOpen) {
      return;
    }
    setSelectedImageFile(null);
    setImageUrl('');
    setImageAlt('');
    const timer = window.setTimeout(() => imageUrlInputRef.current?.focus(), 10);
    return () => window.clearTimeout(timer);
  }, [imagePopoverOpen]);

  const handleBlockTypeChange = useCallback(
    (value: string) => {
      if (value === 'paragraph') {
        editor.chain().focus().setParagraph().run();
      } else {
        const match = value.match(/^heading-(\d)$/);
        if (match) {
          editor.chain().focus().setHeading({ level: Number(match[1]) as 1 | 2 | 3 | 4 }).run();
        }
      }
      setHeadingPopoverOpen(false);
    },
    [editor]
  );

  const handleFontFamilyChange = useCallback(
    (value: string) => {
      if (value === 'inherit') {
        editor.chain().focus().unsetFontFamily().run();
      } else {
        editor.chain().focus().setFontFamily(value).run();
      }
      setFontFamilyPopoverOpen(false);
    },
    [editor]
  );

  const handleFontSizeChange = useCallback(
    (value: string) => {
      if (value === 'inherit') {
        editor.chain().focus().unsetFontSize().run();
      } else {
        editor.chain().focus().setFontSize(value).run();
      }
      setFontSizePopoverOpen(false);
    },
    [editor]
  );

  const handleColorChange = useCallback(
    (value: string) => {
      if (value === 'inherit') {
        editor.chain().focus().unsetColor().run();
      } else {
        editor.chain().focus().setColor(value).run();
      }
      setColorPopoverOpen(false);
    },
    [editor]
  );

  const handleRemoveLink = useCallback(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkPopoverOpen(false);
  }, [editor]);

  const handleLinkSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmed = linkUrl.trim();
      if (trimmed === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        setLinkPopoverOpen(false);
        return;
      }
      const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
      setLinkPopoverOpen(false);
    },
    [editor, linkUrl]
  );

  const handleImageSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmedUrl = imageUrl.trim();
      try {
        setIsInsertingImage(true);
        if (selectedImageFile) {
          await onInsertInlineImage(editor, selectedImageFile, imageAlt || selectedImageFile.name);
        } else if (trimmedUrl) {
          editor
            .chain()
            .focus()
            .setImage({ src: trimmedUrl, alt: imageAlt || undefined })
            .run();
        } else {
          return;
        }
        setImagePopoverOpen(false);
        setImageUrl('');
        setImageAlt('');
        setSelectedImageFile(null);
      } finally {
        setIsInsertingImage(false);
      }
    },
    [editor, imageAlt, imageUrl, onInsertInlineImage, selectedImageFile]
  );

  const handleImageFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setSelectedImageFile(file);
    setImageUrl('');
    setImageAlt((current) => current || file.name);
    event.target.value = '';
  }, []);

  const handleImageFileClear = useCallback(() => {
    setSelectedImageFile(null);
  }, []);

  const handleSetAlignment = useCallback(
    (alignment: TextAlignOption) => {
      if (alignment === 'left') {
        editor.chain().focus().setTextAlign('left').run();
        return;
      }
      editor.chain().focus().setTextAlign(alignment).run();
    },
    [editor]
  );

  const buttons = (
    <>
      <div className="flex items-center gap-0.5">
        <ToolbarButton
          icon={Bold}
          label="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
        />
        <ToolbarButton
          icon={Italic}
          label="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
        />
        <ToolbarButton
          icon={UnderlineIcon}
          label="Underline"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
        />
      </div>
      <div className="flex items-center gap-0.5">
        <ToolbarButton
          icon={List}
          label="Bulleted list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
        />
        <ToolbarButton
          icon={ListOrdered}
          label="Numbered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
        />
      </div>
      <div className="flex items-center gap-0.5">
        <Popover open={linkPopoverOpen} onOpenChange={setLinkPopoverOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  tabIndex={-1}
                  className={toolbarButtonClasses(isLinkActive || linkPopoverOpen)}
                >
                  <Link2 className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">Insert link</TooltipContent>
          </Tooltip>
          <PopoverContent className="w-72 space-y-3" align="start">
            <form className="space-y-3" onSubmit={handleLinkSubmit}>
              <div className="space-y-1">
                <Label htmlFor="compose-link-url" className="text-xs font-medium text-muted-foreground">
                  URL
                </Label>
                <Input
                  id="compose-link-url"
                  ref={linkInputRef}
                  value={linkUrl}
                  onChange={(event) => setLinkUrl(event.target.value)}
                  placeholder="https://example.com"
                  autoComplete="off"
                />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={handleRemoveLink} disabled={!isLinkActive}>
                  Remove link
                </Button>
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setLinkPopoverOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" size="sm">
                    Apply
                  </Button>
                </div>
              </div>
            </form>
          </PopoverContent>
        </Popover>
        <Popover open={imagePopoverOpen} onOpenChange={setImagePopoverOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button type="button" variant="ghost" size="sm" tabIndex={-1} className={toolbarButtonClasses(imagePopoverOpen)}>
                  <ImageIcon className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">Insert image</TooltipContent>
          </Tooltip>
          <PopoverContent className="w-80 space-y-3" align="start">
            <form className="space-y-3" onSubmit={handleImageSubmit}>
              <div className="space-y-1">
                <Label htmlFor="compose-image-url" className="text-xs font-medium text-muted-foreground">
                  Image URL
                </Label>
                <Input
                  id="compose-image-url"
                  ref={imageUrlInputRef}
                  value={imageUrl}
                  onChange={(event) => setImageUrl(event.target.value)}
                  placeholder="https://example.com/image.png"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => inlineImageFileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4" />
                    Upload image
                  </Button>
                  {selectedImageFile && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="gap-1 text-muted-foreground"
                      onClick={handleImageFileClear}
                    >
                      <X className="h-4 w-4" />
                      Clear
                    </Button>
                  )}
                </div>
                <input
                  ref={inlineImageFileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageFileChange}
                />
                {selectedImageFile && <p className="text-xs text-muted-foreground">{selectedImageFile.name}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="compose-image-alt" className="text-xs font-medium text-muted-foreground">
                  Alt text
                </Label>
                <Input
                  id="compose-image-alt"
                  value={imageAlt}
                  onChange={(event) => setImageAlt(event.target.value)}
                  placeholder="Describe the image (optional)"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => setImagePopoverOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={!canInsertImageInline || isInsertingImage}
                  className="gap-2"
                >
                  {isInsertingImage ? 'Inserting…' : 'Insert image'}
                </Button>
              </div>
            </form>
          </PopoverContent>
        </Popover>
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            tabIndex={-1}
            className={cn(
              'h-7 w-7 p-0 text-muted-foreground transition-colors hover:text-foreground',
              showAdvancedFormatting && 'bg-primary/10 text-primary hover:text-primary'
            )}
            onClick={() => setShowAdvancedFormatting((previous) => !previous)}
          >
            {showAdvancedFormatting ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">More formatting</TooltipContent>
      </Tooltip>
      {showAdvancedFormatting && (
        <>
          <Popover open={headingPopoverOpen} onOpenChange={setHeadingPopoverOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    tabIndex={-1}
                    className={toolbarButtonClasses(blockType !== 'paragraph' || headingPopoverOpen)}
                  >
                    <Heading className="h-3.5 w-3.5" />
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="top">Block style ({blockLabel})</TooltipContent>
            </Tooltip>
            <PopoverContent className="w-44 space-y-1 p-2" align="start">
              {HEADING_OPTIONS.map((option) => {
                const isSelected = blockType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleBlockTypeChange(option.value)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent',
                      isSelected && 'bg-accent text-accent-foreground'
                    )}
                  >
                    <span>{option.label}</span>
                    {isSelected && <Check className="h-4 w-4" />}
                  </button>
                );
              })}
            </PopoverContent>
          </Popover>
          <Popover open={fontFamilyPopoverOpen} onOpenChange={setFontFamilyPopoverOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    tabIndex={-1}
                    className={toolbarButtonClasses(currentFontFamily !== 'inherit' || fontFamilyPopoverOpen)}
                  >
                    <Type className="h-3.5 w-3.5" />
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="top">Font family ({currentFontFamilyLabel})</TooltipContent>
            </Tooltip>
            <PopoverContent className="w-52 space-y-1 p-2" align="start">
              {FONT_FAMILY_OPTIONS.map((option) => {
                const isSelected = currentFontFamily === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleFontFamilyChange(option.value)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent',
                      isSelected && 'bg-accent text-accent-foreground'
                    )}
                  >
                    <span>{option.label}</span>
                    {isSelected && <Check className="h-4 w-4" />}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => handleFontFamilyChange('inherit')}
                className={cn(
                  'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent',
                  currentFontFamily === 'inherit' && 'bg-accent text-accent-foreground'
                )}
              >
                <span>System default</span>
                {currentFontFamily === 'inherit' && <Check className="h-4 w-4" />}
              </button>
            </PopoverContent>
          </Popover>
          <Popover open={fontSizePopoverOpen} onOpenChange={setFontSizePopoverOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    tabIndex={-1}
                    className={toolbarButtonClasses(currentFontSize !== 'inherit' || fontSizePopoverOpen)}
                  >
                    <CaseSensitive className="h-3.5 w-3.5" />
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="top">Font size ({currentFontSizeLabel})</TooltipContent>
            </Tooltip>
            <PopoverContent className="w-48 space-y-1 p-2" align="start">
              <button
                type="button"
                onClick={() => handleFontSizeChange('inherit')}
                className={cn(
                  'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent',
                  currentFontSize === 'inherit' && 'bg-accent text-accent-foreground'
                )}
              >
                <span>Default size</span>
                {currentFontSize === 'inherit' && <Check className="h-4 w-4" />}
              </button>
              {FONT_SIZE_OPTIONS.map((option) => {
                const isSelected = currentFontSize === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleFontSizeChange(option)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent',
                      isSelected && 'bg-accent text-accent-foreground'
                    )}
                  >
                    <span>{option}</span>
                    {isSelected && <Check className="h-4 w-4" />}
                  </button>
                );
              })}
            </PopoverContent>
          </Popover>
          <Popover open={colorPopoverOpen} onOpenChange={setColorPopoverOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    tabIndex={-1}
                    className={toolbarButtonClasses(currentColor !== 'inherit' || colorPopoverOpen)}
                  >
                    <Palette className="h-3.5 w-3.5" />
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="top">Text color</TooltipContent>
            </Tooltip>
            <PopoverContent className="w-56 space-y-2 p-3" align="start">
              <div className="grid grid-cols-3 gap-2">
                {COLOR_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleColorChange(option.value)}
                    className={cn(
                      'flex h-9 items-center justify-center rounded-md border border-border/60 text-xs font-medium transition-colors hover:border-primary',
                      option.value === currentColor && 'border-primary bg-primary/10'
                    )}
                    style={option.value !== 'inherit' ? { color: option.value } : undefined}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <ToolbarButton
            icon={Highlighter}
            label="Highlight"
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            isActive={editor.isActive('highlight')}
          />
          <ToolbarButton
            icon={Code}
            label="Inline code"
            onClick={() => editor.chain().focus().toggleCode().run()}
            isActive={editor.isActive('code')}
          />
          <div className="flex items-center gap-0.5">
            {ALIGN_OPTIONS.map((option) => (
              <ToolbarButton
                key={option.value}
                icon={option.icon}
                label={option.label}
                onClick={() => handleSetAlignment(option.value)}
                isActive={currentAlignment === option.value}
              />
            ))}
          </div>
        </>
      )}
    </>
  );

  if (isInline) {
    return buttons;
  }

  return (
    <section className="pt-2">
      <div className="flex flex-wrap items-center gap-1">
        {buttons}
        <div className="ml-auto flex items-center gap-0.5">
          <ToolbarButton
            icon={Undo2}
            label="Undo"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!canUndo}
          />
          <ToolbarButton
            icon={Redo2}
            label="Redo"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!canRedo}
          />
        </div>
      </div>
    </section>
  );
}
